(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-treedix-product-experience]';
  const THUMB_SELECTOR = '[data-pxp-thumb]';
  const IMAGE_THUMB_SELECTOR = `${THUMB_SELECTOR}:not([data-tl-video-open])`;
  const VIDEO_THUMB_SELECTOR = `${THUMB_SELECTOR}[data-tl-video-open]`;
  const RAIL_CLASS = 'treedix-pdp-thumb-rail';
  const ARROW_CLASS = 'treedix-pdp-thumb-arrow';

  function canonicalImageKey(url) {
    const raw = String(url || '').trim();
    if (!raw) return '';

    let pathname = raw;
    try {
      const parsed = new URL(raw, window.location.origin);
      pathname = parsed.pathname || raw;
    } catch (error) {
      pathname = raw.split('?')[0].split('#')[0];
    }

    pathname = decodeURIComponent(pathname).split('?')[0].split('#')[0].toLowerCase();
    const filename = pathname.split('/').filter(Boolean).pop() || pathname;

    return filename
      .replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|master|original)(?=\.)/g, '')
      .replace(/_\d+x(?:\d+)?(?=\.)/g, '')
      .replace(/_crop_[a-z]+(?=\.)/g, '')
      .replace(/\.(jpg|jpeg|png|webp|gif)(?:.*)?$/, '.$1');
  }

  function imageUrl(src, width) {
    if (!src) return '';
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (error) {
      return src;
    }
  }

  function thumbKey(thumb) {
    if (!thumb) return '';
    const img = thumb.querySelector('img');
    return canonicalImageKey(thumb.dataset.imageUrl || thumb.dataset.zoomUrl || (img && (img.currentSrc || img.src)) || '');
  }

  function setActiveThumb(thumbs, activeThumb) {
    thumbs.forEach((thumb) => {
      const active = thumb === activeThumb;
      thumb.classList.toggle('is-active', active);
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
  }

  function updateMainImage(root, thumb) {
    const main = root.querySelector('[data-pxp-main-image-container]');
    const image = main && main.querySelector('img');
    const zoom = root.querySelector('[data-pxp-zoom]');
    const imageUrlValue = thumb && thumb.dataset.imageUrl;
    const zoomUrlValue = thumb && thumb.dataset.zoomUrl;
    const alt = thumb && thumb.dataset.imageAlt;

    if (!main || !image || !imageUrlValue) return;
    main.classList.add('is-loading');
    image.addEventListener('load', () => main.classList.remove('is-loading'), { once: true });
    image.src = imageUrlValue;
    image.srcset = '';
    if (alt) image.alt = alt;
    if (zoom && zoomUrlValue) zoom.href = zoomUrlValue;
  }

  function createThumb(src, index, title) {
    const button = document.createElement('button');
    button.className = 'tl-pfinal-thumb';
    button.type = 'button';
    button.dataset.pxpThumb = '';
    button.dataset.imageUrl = imageUrl(src, 1200);
    button.dataset.zoomUrl = imageUrl(src, 1600);
    button.dataset.imageKey = canonicalImageKey(src);
    button.dataset.imageAlt = `${title} image ${index + 1}`;
    button.setAttribute('aria-label', `View product image ${index + 1}`);

    const img = document.createElement('img');
    img.className = 'tl-pfinal-img';
    img.loading = index === 0 ? 'eager' : 'lazy';
    img.alt = title;
    img.src = imageUrl(src, 180);
    img.sizes = '76px';

    button.appendChild(img);
    return button;
  }

  function ensureActiveThumb(root) {
    const imageThumbs = Array.from(root.querySelectorAll(IMAGE_THUMB_SELECTOR));
    if (!imageThumbs.length) return;

    const active = imageThumbs.find((thumb) => thumb.classList.contains('is-active'));
    if (active) {
      active.setAttribute('aria-current', 'true');
      return;
    }

    imageThumbs[0].classList.add('is-active');
    imageThumbs[0].setAttribute('aria-current', 'true');
  }

  function removeDuplicateImageThumbs(root) {
    const thumbs = Array.from(root.querySelectorAll(IMAGE_THUMB_SELECTOR));
    const seen = new Set();
    let removed = 0;

    thumbs.forEach((thumb) => {
      const key = thumbKey(thumb);
      thumb.dataset.imageKey = key;
      if (!key) return;

      if (seen.has(key)) {
        thumb.remove();
        removed += 1;
        return;
      }

      seen.add(key);
    });

    root.dataset.galleryDuplicateThumbsRemoved = String(removed);
    ensureActiveThumb(root);
    return seen;
  }

  function removeDuplicateVideoThumbs(root, imageKeys) {
    const videoThumbs = Array.from(root.querySelectorAll(VIDEO_THUMB_SELECTOR));
    let removed = 0;

    videoThumbs.forEach((thumb) => {
      const key = thumbKey(thumb);
      if (!key || !imageKeys.has(key)) return;
      thumb.remove();
      removed += 1;
    });

    root.dataset.galleryDuplicateVideoThumbsRemoved = String(removed);
  }

  function cleanGalleryThumbs(root) {
    const imageKeys = removeDuplicateImageThumbs(root);
    removeDuplicateVideoThumbs(root, imageKeys);
    ensureActiveThumb(root);
    return imageKeys;
  }

  function uniqueProductImages(images) {
    const unique = [];
    const seen = new Set();

    images.forEach((src) => {
      const key = canonicalImageKey(src);
      if (!src || !key || seen.has(key)) return;
      seen.add(key);
      unique.push({ src, key });
    });

    return unique;
  }

  function rebuildImageThumbs(root, product) {
    const thumbs = root.querySelector('.tl-pfinal-thumbs');
    if (!thumbs || !product || !Array.isArray(product.images)) return false;

    const images = uniqueProductImages(product.images);
    if (!images.length) return false;

    const currentActive = root.querySelector(`${IMAGE_THUMB_SELECTOR}.is-active`);
    const currentKey = thumbKey(currentActive);
    const videoThumbs = Array.from(root.querySelectorAll(VIDEO_THUMB_SELECTOR));
    const imageKeys = new Set(images.map((image) => image.key));

    root.querySelectorAll(IMAGE_THUMB_SELECTOR).forEach((thumb) => thumb.remove());

    let activeThumb = null;
    images.forEach((image, index) => {
      const thumb = createThumb(image.src, index, product.title || 'Product');
      if ((currentKey && image.key === currentKey) || (!currentKey && index === 0)) {
        activeThumb = thumb;
      }
      thumbs.insertBefore(thumb, videoThumbs[0] || null);
    });

    removeDuplicateVideoThumbs(root, imageKeys);
    const finalThumbs = Array.from(root.querySelectorAll(IMAGE_THUMB_SELECTOR));
    setActiveThumb(finalThumbs, activeThumb || finalThumbs[0]);

    if (!currentKey && finalThumbs[0]) updateMainImage(root, finalThumbs[0]);

    root.dataset.galleryImageCount = String(finalThumbs.length);
    root.dataset.galleryRebuiltFromProductJson = 'true';
    return true;
  }

  function createArrow(direction) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `${ARROW_CLASS} ${ARROW_CLASS}--${direction}`;
    button.dataset.galleryThumbArrow = direction;
    button.setAttribute('aria-label', direction === 'prev' ? 'Show previous thumbnails' : 'Show next thumbnails');
    button.innerHTML = direction === 'prev' ? '&#9650;' : '&#9660;';
    return button;
  }

  function ensureThumbRail(root) {
    const thumbs = root.querySelector('.tl-pfinal-thumbs');
    if (!thumbs) return null;

    let rail = thumbs.closest(`.${RAIL_CLASS}`);
    if (!rail) {
      rail = document.createElement('div');
      rail.className = RAIL_CLASS;
      thumbs.parentNode.insertBefore(rail, thumbs);
      rail.appendChild(createArrow('prev'));
      rail.appendChild(thumbs);
      rail.appendChild(createArrow('next'));
    }

    return rail;
  }

  function thumbStep(root) {
    const first = root.querySelector(IMAGE_THUMB_SELECTOR);
    const thumbs = root.querySelector('.tl-pfinal-thumbs');
    if (!first || !thumbs) return 88;
    const styles = window.getComputedStyle(thumbs);
    const gap = parseFloat(styles.rowGap || styles.gap || '0') || 0;
    return first.getBoundingClientRect().height + gap;
  }

  function syncThumbRail(root) {
    const rail = ensureThumbRail(root);
    const thumbs = root.querySelector('.tl-pfinal-thumbs');
    const main = root.querySelector('[data-pxp-main-image-container]');
    if (!rail || !thumbs || !main) return;

    const mainHeight = Math.round(main.getBoundingClientRect().height);
    if (mainHeight > 0) rail.style.setProperty('--treedix-pdp-gallery-height', `${mainHeight}px`);

    requestAnimationFrame(() => {
      const hasOverflow = thumbs.scrollHeight > thumbs.clientHeight + 2;
      rail.classList.toggle('has-overflow', hasOverflow);

      const prev = rail.querySelector('[data-gallery-thumb-arrow="prev"]');
      const next = rail.querySelector('[data-gallery-thumb-arrow="next"]');
      if (!prev || !next) return;

      prev.disabled = !hasOverflow || thumbs.scrollTop <= 1;
      next.disabled = !hasOverflow || thumbs.scrollTop + thumbs.clientHeight >= thumbs.scrollHeight - 1;
      root.dataset.galleryThumbOverflow = String(hasOverflow);
    });
  }

  function moveThumbRail(root, direction) {
    const thumbs = root.querySelector('.tl-pfinal-thumbs');
    if (!thumbs) return;
    const distance = thumbStep(root);
    thumbs.scrollTo({ top: thumbs.scrollTop + distance * direction, behavior: 'smooth' });
    window.setTimeout(() => syncThumbRail(root), 260);
  }

  async function hydrateMissingThumbs(root) {
    const thumbs = root.querySelector('.tl-pfinal-thumbs');
    const productUrl = root.dataset.productUrl;
    if (!thumbs || !productUrl || thumbs.dataset.galleryPolished === 'true') return;
    thumbs.dataset.galleryPolished = 'true';

    cleanGalleryThumbs(root);
    syncThumbRail(root);

    try {
      const response = await fetch(`${productUrl}.js`, { credentials: 'same-origin' });
      if (!response.ok) return;
      const product = await response.json();
      const rebuilt = rebuildImageThumbs(root, product);
      if (!rebuilt) cleanGalleryThumbs(root);
      syncThumbRail(root);
    } catch (error) {
      console.warn('[TREEDIX PDP gallery] Could not load full product image list.', error);
      cleanGalleryThumbs(root);
      syncThumbRail(root);
    }
  }

  function bindGallery(root) {
    if (!root || root.dataset.galleryPolishInitialized === 'true') return;
    root.dataset.galleryPolishInitialized = 'true';

    root.addEventListener('click', (event) => {
      const arrow = event.target.closest('[data-gallery-thumb-arrow]');
      if (arrow && root.contains(arrow)) {
        event.preventDefault();
        moveThumbRail(root, arrow.dataset.galleryThumbArrow === 'prev' ? -1 : 1);
        return;
      }

      const thumb = event.target.closest(THUMB_SELECTOR);
      if (!thumb || !root.contains(thumb)) return;
      if (thumb.matches('[data-tl-video-open]')) return;
      event.preventDefault();
      const thumbs = Array.from(root.querySelectorAll(IMAGE_THUMB_SELECTOR));
      setActiveThumb(thumbs, thumb);
      updateMainImage(root, thumb);
      thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      syncThumbRail(root);
    });

    const thumbs = root.querySelector('.tl-pfinal-thumbs');
    if (thumbs) thumbs.addEventListener('scroll', () => syncThumbRail(root), { passive: true });
    window.addEventListener('resize', () => syncThumbRail(root), { passive: true });

    hydrateMissingThumbs(root);
    window.setTimeout(() => syncThumbRail(root), 400);
  }

  function init(scope) {
    (scope || document).querySelectorAll(ROOT_SELECTOR).forEach(bindGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
